import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { extend } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import Search from 'flarum/forum/components/Search';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import PostStream from 'flarum/forum/components/PostStream';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';

const fallback = {
  headline: 'Fidan, Bitki ve Tarım Firmalarını Fidanligi.com’da Keşfedin',
  body: 'Fidan üreticilerini, peyzaj firmalarını, tarım işletmelerini ve ürün ilanlarını inceleyin. Firmanızı ekleyin ve teklif taleplerini takip edin.',
  primaryUrl: 'https://www.fidanligi.com/',
  primaryLabel: 'Fidanligi.com’a Git',
  secondaryUrl: 'https://www.fidanligi.com/registration/',
  secondaryLabel: 'Ücretsiz Üyelik',
};

function attribute(name, defaultValue) {
  const value = app.forum.attribute(name);
  return value === undefined || value === null ? defaultValue : value;
}

function t(key, fallback = '') {
  const value = app.translator.trans(`gardensel-ads.forum.${key}`);
  return typeof value === 'string' && !value.startsWith('gardensel-ads.') ? value : fallback;
}

function cleanUrl(value, defaultValue) {
  try {
    const url = new URL(String(value || ''), window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : defaultValue;
  } catch (_) {
    return defaultValue;
  }
}

function redrawIfMounted(component) {
  if (!component.destroyed) m.redraw();
}

function visibleTag(tag) {
  return !tag?.isHidden?.();
}

function placementEnabled(placement) {
  if (!attribute('gardenselAdsEnabled', false)) return false;
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  if (isMobile && !attribute('gardenselAdsMobileEnabled', true)) return false;
  if (!isMobile && !attribute('gardenselAdsDesktopEnabled', true)) return false;
  return String(attribute('gardenselAdsPlacements', 'index,after_first_post,long_discussion'))
    .split(',')
    .map((value) => value.trim())
    .includes(placement);
}

function promoCard(placement) {
  const headline = String(attribute('gardenselAdsHeadline', fallback.headline)).slice(0, 180);
  const body = String(attribute('gardenselAdsBody', fallback.body)).slice(0, 400);
  const primaryUrl = cleanUrl(attribute('gardenselAdsPrimaryUrl', fallback.primaryUrl), fallback.primaryUrl);
  const secondaryUrl = cleanUrl(attribute('gardenselAdsSecondaryUrl', fallback.secondaryUrl), fallback.secondaryUrl);
  return (
    <aside className={`gardensel-promo-card gardensel-promo-card--${placement}`} aria-label={t('promo.aria', 'Kurumsal tanıtım')}>
      <span className="gardensel-promo-card__visual" aria-hidden="true">
        <i className="fas fa-seedling" />
      </span>
      <div className="gardensel-promo-card__copy">
        <span className="gardensel-promo-card__label">{t('promo.label', 'Kurumsal Tanıtım · Fidanligi')}</span>
        <strong className="gardensel-promo-card__headline">{headline}</strong>
        <span className="gardensel-promo-card__body">{body}</span>
      </div>
      <div className="gardensel-promo-card__actions">
        <a className="Button Button--primary" href={primaryUrl} rel="noopener">
          {String(attribute('gardenselAdsPrimaryLabel', fallback.primaryLabel)).slice(0, 80)}
        </a>
        <a className="Button" href={secondaryUrl} rel="noopener">
          {String(attribute('gardenselAdsSecondaryLabel', fallback.secondaryLabel)).slice(0, 80)}
        </a>
      </div>
    </aside>
  );
}

class GlossarySearchSource {
  constructor() {
    this.results = [];
    this.requestId = 0;
  }
  search(query) {
    const requestId = ++this.requestId;
    return fetch(`/sozluk/api/search?q=${encodeURIComponent(query)}`)
      .then((response) => (response.ok ? response.json() : { data: [] }))
      .then((payload) => {
        if (requestId === this.requestId) this.results = Array.isArray(payload.data) ? payload.data : [];
      })
      .catch(() => {
        if (requestId === this.requestId) this.results = [];
      });
  }
  view(query) {
    return [
      <li key="gardensel-glossary-header" className="Dropdown-header">{t('search.glossary_results', 'Sözlük sonuçları')}</li>,
      ...this.results.map((term) => (
        <li key={`gardensel-glossary-${term.slug}`}>
          <a href={`/sozluk/${encodeURIComponent(term.slug)}`}>
            <i className="icon fas fa-book" />
            <span className="Search-result-title">{term.name}</span>
          </a>
        </li>
      )),
      <li key="gardensel-glossary-all">
        <a href={`/sozluk/ara?q=${encodeURIComponent(query)}`}>
          <i className="icon fas fa-search" /> {t('search.all_glossary_results', 'Sözlükte tüm sonuçları göster')}
        </a>
      </li>,
    ];
  }
}

function categoryPayload() {
  return attribute('gardenselCategoryGroups', { groups: [] }) || { groups: [] };
}

function formatDate(value) {
  if (!value) return 'Henüz aktivite yok';
  try {
    return new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium' }).format(new Date(String(value).replace(' ', 'T')));
  } catch (_) {
    return String(value);
  }
}

function primaryDiscussionTag(discussion) {
  const tags = discussion?.tags?.() || [];
  return tags.find(visibleTag) || tags[0] || null;
}

class DiscussionHub extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.destroyed = false;
    this.loading = true;
    this.terms = [];
    this.sameCategory = [];
    this.similar = [];
    this.mostDiscussed = [];
    this.recentlyUpdated = [];
    this.newestTopics = [];
    const discussion = this.attrs.discussion;
    const title = discussion?.title?.() || '';
    const primaryTag = primaryDiscussionTag(discussion);
    const categoryRequest = (sort) =>
      primaryTag
        ? app.store.find('discussions', { filter: { tag: primaryTag.slug() }, sort, page: { limit: 30 } })
        : Promise.resolve([]);
    const requests = [
      fetch(`/sozluk/api/related?q=${encodeURIComponent(title)}`).then((response) => (response.ok ? response.json() : { data: [] })),
      categoryRequest('-lastPostedAt'),
      app.store.find('discussions', { filter: { q: title }, page: { limit: 30 } }),
      categoryRequest('-commentCount'),
      categoryRequest('-lastPostedAt'),
      categoryRequest('-createdAt'),
    ];
    Promise.allSettled(requests).then((results) => {
      this.terms = results[0].status === 'fulfilled' ? results[0].value.data || [] : [];
      const currentId = discussion?.id?.();
      this.sameCategory = results[1]?.status === 'fulfilled' ? (results[1].value || []).filter((item) => item.id() !== currentId) : [];
      this.similar = results[2]?.status === 'fulfilled' ? (results[2].value || []).filter((item) => item.id() !== currentId) : [];
      this.mostDiscussed = results[3]?.status === 'fulfilled' ? results[3].value || [] : [];
      this.recentlyUpdated = results[4]?.status === 'fulfilled' ? results[4].value || [] : [];
      this.newestTopics = results[5]?.status === 'fulfilled' ? results[5].value || [] : [];
      this.loading = false;
      redrawIfMounted(this);
    });
  }
  onremove() {
    this.destroyed = true;
  }
  discussionLinks(items) {
    return items.map((discussion) => (
      <Link key={`discussion-${discussion.id()}`} href={app.route.discussion(discussion)}>
        {discussion.title()}
      </Link>
    ));
  }
  view() {
    const discussion = this.attrs.discussion;
    const title = discussion?.title?.() || '';
    const primaryTag = primaryDiscussionTag(discussion);
    const parent = primaryTag?.parent?.();
    const group = (categoryPayload().groups || []).find((item) => (item.tag_ids || []).map(String).includes(String(primaryTag?.id?.())));

    const currentId = String(discussion?.id?.() || '');
    const normalizeSlug = (item) => String(item?.slug?.() || '').trim().toLocaleLowerCase('tr-TR');
    const usedDiscussionIds = new Set(currentId ? [currentId] : []);
    const currentSlug = normalizeSlug(discussion);
    const usedDiscussionSlugs = new Set(currentSlug ? [currentSlug] : []);
    const usableDiscussion = (item) =>
      item &&
      String(item.id?.() || '') !== currentId &&
      !(item.isHidden?.() || false) &&
      item.isApproved?.() !== false &&
      String(item.title?.() || '').trim();

    const takeUnique = (items, limit) => {
      const selected = [];

      for (const item of items || []) {
        const id = String(item?.id?.() || '');
        const slug = normalizeSlug(item);

        if (!id || usedDiscussionIds.has(id) || (slug && usedDiscussionSlugs.has(slug)) || !usableDiscussion(item)) continue;

        usedDiscussionIds.add(id);
        if (slug) usedDiscussionSlugs.add(slug);
        selected.push(item);

        if (selected.length >= limit) break;
      }

      return selected;
    };

    const timestamp = (value) => {
      const time = new Date(value || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    const categoryPool = (this.sameCategory || []).filter(usableDiscussion);

    const sameCategoryLinks = takeUnique(
      [...categoryPool].sort(
        (a, b) =>
          timestamp(b.lastPostedAt?.() || b.createdAt?.()) -
          timestamp(a.lastPostedAt?.() || a.createdAt?.())
      ),
      6
    );
    const similarLinks = takeUnique(this.similar || [], 6);

    const mostDiscussed = takeUnique(
      [...(this.mostDiscussed || [])].sort(
        (a, b) => Number(b.commentCount?.() || 0) - Number(a.commentCount?.() || 0)
      ),
      6
    );

    const recentlyUpdated = takeUnique(
      [...(this.recentlyUpdated || [])].sort(
        (a, b) =>
          timestamp(b.lastPostedAt?.() || b.createdAt?.()) -
          timestamp(a.lastPostedAt?.() || a.createdAt?.())
      ),
      6
    );

    const newestTopics = takeUnique(
      [...(this.newestTopics || [])].sort(
        (a, b) => timestamp(b.createdAt?.()) - timestamp(a.createdAt?.())
      ),
      6
    );
    return (
      <section className="GardenselHub" aria-labelledby="related-terms-title">
        <div className="GardenselHub-heading">
          <div>
            <span>{t('hub.kicker', 'Bilgi Merkezi')}</span>
            <h2 id="related-terms-title">{t('hub.continue', 'İlgili Konular ve Faydalı Kaynaklar')}</h2>
          </div>
          <a href={`/sozluk/ara?q=${encodeURIComponent(title)}`}>{t('hub.glossary_continue', 'Sözlükte devamını oku')}</a>
        </div>
        {this.loading ? (
          <LoadingIndicator size="small" />
        ) : (
          <div className="GardenselHub-columns">
            <div>
              <h3>{t('hub.related_terms', 'İlgili sözlük terimleri')}</h3>
              {this.terms.length ? (
                <nav>
                  {this.terms.slice(0, 6).map((term) => (
                    <a key={`hub-term-${term.slug}`} href={`/sozluk/${encodeURIComponent(term.slug)}`}>
                      <i className="fas fa-book-open" />
                      {term.name}
                    </a>
                  ))}
                </nav>
              ) : (
                <div className="GardenselHub-empty">
                  <p>{t('hub.no_terms', 'Bu konu için doğrudan eşleşen sözlük terimi bulunamadı.')}</p>
                  <a href={`/sozluk/ara?q=${encodeURIComponent(title)}`}>{t('hub.glossary_search', 'Sözlükte ara')}</a>
                  {primaryTag ? <Link href={app.route.tag(primaryTag)}>{t('hub.category_go', 'Kategoriye git')}</Link> : null}
                  <Link href={`/?q=${encodeURIComponent(title)}`}>{t('hub.forum_search', 'Forum aramasına dön')}</Link>
                </div>
              )}
            </div>
            {sameCategoryLinks.length ? (
              <div>
                <h3>{t('hub.same_category', 'Aynı kategorideki konular')}</h3>
                <nav>{this.discussionLinks(sameCategoryLinks)}</nav>
              </div>
            ) : null}
            {similarLinks.length ? (
              <div>
                <h3>{t('hub.similar', 'Benzer konular')}</h3>
                <nav>{this.discussionLinks(similarLinks)}</nav>
              </div>
            ) : null}

            {mostDiscussed.length ? (
              <div>
                <h3>{t('hub.most_discussed', 'En çok konuşulanlar')}</h3>
                <nav>{this.discussionLinks(mostDiscussed)}</nav>
              </div>
            ) : null}

            {recentlyUpdated.length ? (
              <div>
                <h3>{t('hub.recently_updated', 'Son güncellenenler')}</h3>
                <nav>{this.discussionLinks(recentlyUpdated)}</nav>
              </div>
            ) : null}

            {newestTopics.length ? (
              <div>
                <h3>{t('hub.newest_topics', 'Yeni konular')}</h3>
                <nav>{this.discussionLinks(newestTopics)}</nav>
              </div>
            ) : null}
          </div>
        )}
        <div className="GardenselHub-paths">
          {primaryTag ? <Link href={app.route.tag(primaryTag)}>Kategori: {primaryTag.name()}</Link> : null}
          {parent ? <Link href={app.route.tag(parent)}>Üst kategori: {parent.name()}</Link> : null}
          {group ? <Link href={app.route('gardenselCategoryGroup', { group: group.slug })}>Ana grup: {group.name}</Link> : null}
          {(group?.glossary || []).slice(0, 2).map((slug) => (
            <a key={`hub-glossary-${slug}`} href={`/sozluk/kategori/${slug}`}>{t('hub.related_glossary_category', 'İlgili sözlük kategorisi')}</a>
          ))}
        </div>
      </section>
    );
  }
}

class RelatedTerms extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.destroyed = false;
    this.loading = true;
    this.terms = [];
    fetch(`/sozluk/api/related?q=${encodeURIComponent(this.attrs.title || '')}`)
      .then((response) => (response.ok ? response.json() : { data: [] }))
      .then((payload) => {
        this.terms = payload.data || [];
        this.loading = false;
        redrawIfMounted(this);
      })
      .catch(() => {
        this.loading = false;
        redrawIfMounted(this);
      });
  }
  onremove() {
    this.destroyed = true;
  }
  view() {
    return (
      <section className="GardenselHub" aria-labelledby="related-terms-title">
        <div className="GardenselHub-heading">
          <div>
            <span>Bilgi Merkezi</span>
            <h2 id="related-terms-title">İlgili Terimler</h2>
          </div>
          <a href={`/sozluk/ara?q=${encodeURIComponent(this.attrs.title || '')}`}>Sözlükte ara</a>
        </div>
        {this.loading ? (
          <LoadingIndicator size="small" />
        ) : this.terms.length ? (
          <div className="GardenselHub-grid">
            {this.terms.slice(0, 6).map((term) => (
              <a key={`related-term-${term.slug}`} href={`/sozluk/${encodeURIComponent(term.slug)}`}>
                <i className="fas fa-book-open" />
                <strong>{term.name}</strong>
              </a>
            ))}
          </div>
        ) : (
          <p>
            Bu konu için doğrudan eşleşen terim bulunamadı. <a href="/sozluk/">Sözlüğü keşfedin.</a>
          </p>
        )}
      </section>
    );
  }
}

class HomeKnowledge extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.destroyed = false;
    this.data = null;
    fetch('/sozluk/api/home')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        this.data = data;
        redrawIfMounted(this);
      })
      .catch(() => {
        this.data = false;
        redrawIfMounted(this);
      });
  }
  onremove() {
    this.destroyed = true;
  }
  view() {
    if (this.data === null)
      return (
        <section className="GardenselKnowledge">
          <LoadingIndicator size="small" />
        </section>
      );
    const dayDiscussion = app.discussions.getPages().flatMap((page) => page.items || [])[
      new Date().getDate() % Math.max(1, app.discussions.getPages().flatMap((page) => page.items || []).length)
    ];
    return (
      <section className="GardenselKnowledge">
        <div className="GardenselSection-title">
          <div>
            <span className="eyebrow">Her ziyarette yeni bir keşif</span>
            <h2>Gardensel Bilgi Merkezi</h2>
          </div>
        </div>
        <div className="GardenselKnowledge-grid">
          {this.data?.day ? (
            <a className="GardenselFeature" href={`/sozluk/${this.data.day.slug}`}>
              <span>Günün Terimi</span>
              <strong>{this.data.day.name}</strong>
              <p>{String(this.data.day.description || '').slice(0, 120)}</p>
            </a>
          ) : null}
          {dayDiscussion ? (
            <Link className="GardenselFeature" href={app.route.discussion(dayDiscussion)}>
              <span>Günün Konusu</span>
              <strong>{dayDiscussion.title()}</strong>
              <p>{Number(dayDiscussion.commentCount() || 0).toLocaleString('tr-TR')} yanıtla toplulukta konuşuluyor.</p>
            </Link>
          ) : null}
          {this.data?.random ? (
            <a className="GardenselFeature" href={`/sozluk/${this.data.random.slug}`}>
              <span>Rastgele Bilgi</span>
              <strong>{this.data.random.name}</strong>
              <p>{String(this.data.random.description || '').slice(0, 120)}</p>
            </a>
          ) : null}
          <a className="GardenselFeature" href="/tr/">
            <span>Bitki Veritabanı</span>
            <strong>Son Makaleleri Keşfedin</strong>
            <p>Bitkiler, yetiştiricilik ve tarım uygulamaları için bilgi arşivi.</p>
          </a>
        </div>
        {this.data?.latest?.length ? (
          <div className="GardenselTerms">
            <div>
              <h3>Son Eklenen Terimler</h3>
              <a href="/sozluk/">Tüm sözlük</a>
            </div>
            <nav>
              {this.data.latest.map((term) => (
                <a key={`latest-term-${term.slug}`} href={`/sozluk/${encodeURIComponent(term.slug)}`}>{term.name}</a>
              ))}
            </nav>
          </div>
        ) : null}

      </section>
    );
  }
}

function categoryBranch(tag, tags, matches, depth = 0, visited = new Set()) {
  if (depth > 12 || visited.has(tag.id())) return null;
  const nextVisited = new Set(visited).add(tag.id());
  const children = tags.filter((child) => child.parent && child.parent() && child.parent().id() === tag.id());
  const visibleChildren = children.filter((child) => matches(child) || hasMatchingDescendant(child, tags, matches, nextVisited));
  if (!matches(tag) && !visibleChildren.length) return null;
  return (
    <div key={`category-${tag.id()}`} className={`CategoryTree-node CategoryTree-node--depth-${Math.min(depth, 5)}`}>
      <Link href={app.route.tag(tag)}>
        <span>
          {depth === 0 ? <i className={tag.icon() || 'fas fa-folder'} /> : null}
          <strong>{tag.name()}</strong>
        </span>
        <small>{Number(tag.discussionCount() || 0).toLocaleString('tr-TR')} konu</small>
      </Link>
      {visibleChildren.length ? (
        <div className="CategoryTree-branch">{visibleChildren.map((child) => categoryBranch(child, tags, matches, depth + 1, nextVisited)).filter(Boolean)}</div>
      ) : null}
    </div>
  );
}

function hasMatchingDescendant(tag, tags, matches, visited = new Set()) {
  if (visited.has(tag.id())) return false;
  const nextVisited = new Set(visited).add(tag.id());
  return tags.some(
    (child) =>
      child.parent &&
      child.parent() &&
      child.parent().id() === tag.id() &&
      (matches(child) || hasMatchingDescendant(child, tags, matches, nextVisited))
  );
}

class CategoryDirectory extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.query = '';
    this.destroyed = false;
    this.loading = true;
    app.store.find('tags')
      .catch(() => [])
      .finally(() => {
        this.loading = false;
        redrawIfMounted(this);
      });
    app.history.push('gardenselCategories', 'Kategoriler');
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    app.setTitle('Kategori Dizini');
    app.setTitleCount(0);
  }
  onremove() {
    this.destroyed = true;
  }
  view() {
    const tags = app.store.all('tags').filter(visibleTag);
    const query = this.query.trim().toLocaleLowerCase('tr-TR');
    const groups = (categoryPayload().groups || []).filter(
      (group) =>
        !query ||
        `${group.name} ${group.description}`.toLocaleLowerCase('tr-TR').includes(query) ||
        tags.some((tag) => (group.tag_ids || []).map(String).includes(String(tag.id())) && tag.name().toLocaleLowerCase('tr-TR').includes(query))
    );
    return (
      <div className="GardenselDirectory">
        <header className="GardenselDirectory-hero">
          <div className="container">
            <span className="eyebrow">{Number(categoryPayload().tag_count || tags.length).toLocaleString('tr-TR')} teknik kategori · {Number((categoryPayload().groups || []).length).toLocaleString('tr-TR')} ana grup</span>
            <h1>Kategori Dizini</h1>
            <p>Tarım, bahçe, peyzaj, bitki sağlığı ve ormancılık konularını ağaç görünümünde keşfedin.</p>
            <label>
              <i className="fas fa-search" />
              <input
                className="FormControl"
                type="search"
                placeholder="Kategori ara…"
                value={this.query}
                oninput={(event) => {
                  this.query = event.target.value;
                }}
              />
            </label>
          </div>
        </header>
        <main className="container GardenselDirectory-main">
          {this.loading ? (
            <LoadingIndicator />
          ) : groups.length ? (
            groups.map((group) => (
              <Link key={`category-group-${group.slug}`} className="CategoryGroup-card" href={app.route('gardenselCategoryGroup', { group: group.slug })}>
                <i className={group.icon || 'fas fa-folder'} />
                <span>
                  <strong>{group.name}</strong>
                  <small>{group.description}</small>
                </span>
                <dl>
                  <div>
                    <dt>Alt kategori</dt>
                    <dd>{Number(group.subcategory_count || 0).toLocaleString('tr-TR')}</dd>
                  </div>
                  <div>
                    <dt>Konu</dt>
                    <dd>{Number(group.discussion_count || 0).toLocaleString('tr-TR')}</dd>
                  </div>
                  <div>
                    <dt>Son aktivite</dt>
                    <dd>{formatDate(group.last_activity)}</dd>
                  </div>
                </dl>
              </Link>
            ))
          ) : (
            <div className="GardenselEmpty">
              <h2>Sonuç bulunamadı</h2>
              <p>Başka bir kategori adıyla tekrar deneyin.</p>
            </div>
          )}
        </main>
      </div>
    );
  }
}

class CategoryGroupPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.query = '';
    this.destroyed = false;
    this.loading = true;
    this.featured = [];
    app.store
      .find('tags')
      .then(() => {
        const group = this.group();
        const tags = this.tags(group).sort((a, b) => Number(b.discussionCount() || 0) - Number(a.discussionCount() || 0));
        const candidates = tags.filter((tag) => Number(tag.discussionCount() || 0) > 0).slice(0, 10);
        return Promise.allSettled(
          candidates.map((tag) => app.store.find('discussions', { filter: { tag: tag.slug() }, sort: '-commentCount', page: { limit: 3 } }))
        );
      })
      .catch(() => [])
      .then((sets) => {
        const seen = new Set();
        this.featured = (sets || [])
          .flatMap((set) => (set.status === 'fulfilled' ? set.value : []))
          .filter((item) => !seen.has(item.id()) && seen.add(item.id()))
          .slice(0, 6);
        this.loading = false;
        redrawIfMounted(this);
      });
    app.history.push('gardenselCategoryGroup', 'Kategori');
  }
  group() {
    return (categoryPayload().groups || []).find((item) => item.slug === m.route.param('group'));
  }
  tags(group) {
    const ids = new Set((group?.tag_ids || []).map(String));
    return app.store.all('tags').filter((tag) => ids.has(String(tag.id())) && visibleTag(tag));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    const group = this.group();
    app.setTitle(group ? group.name : 'Kategori');
    app.setTitleCount(0);
  }
  onremove() {
    this.destroyed = true;
  }
  view() {
    const group = this.group();
    if (!group)
      return (
        <div className="container GardenselEmpty">
          <h1>Kategori bulunamadı</h1>
          <Link href={app.route('gardenselCategories')}>Kategori dizinine dönün</Link>
        </div>
      );
    const query = this.query.trim().toLocaleLowerCase('tr-TR');
    const tags = this.tags(group)
      .filter((tag) => !query || `${tag.name()} ${tag.description() || ''}`.toLocaleLowerCase('tr-TR').includes(query))
      .sort((a, b) => Number(b.discussionCount() || 0) - Number(a.discussionCount() || 0));
    return (
      <div className="GardenselDirectory">
        <header className="GardenselDirectory-hero">
          <div className="container">
            <nav className="GardenselBreadcrumb">
              <Link href="/">Forum</Link>
              <span>›</span>
              <Link href={app.route('gardenselCategories')}>Kategoriler</Link>
              <span>›</span>
              <span>{group.name}</span>
            </nav>
            <span className="eyebrow">Gardensel ana kategorisi</span>
            <h1>{group.name}</h1>
            <p>{group.description}</p>
            <div className="CategoryGroup-stats">
              <b>{group.subcategory_count} alt kategori</b>
              <b>{Number(group.discussion_count).toLocaleString('tr-TR')} konu</b>
              <b>Son aktivite: {formatDate(group.last_activity)}</b>
            </div>
            <label>
              <i className="fas fa-search" />
              <input
                className="FormControl"
                type="search"
                placeholder="Bu grupta kategori ara…"
                value={this.query}
                oninput={(e) => (this.query = e.target.value)}
              />
            </label>
          </div>
        </header>
        <main className="container CategoryGroup-page">
          <section className="CategoryGroup-section">
            <h2>Alt kategoriler</h2>
            <div className="CategoryGroup-tags">
              {tags.map((tag) => (
                <Link key={`group-tag-${tag.id()}`} href={app.route.tag(tag)}>
                  <strong>{tag.name()}</strong>
                  <span>{Number(tag.discussionCount() || 0).toLocaleString('tr-TR')} konu</span>
                </Link>
              ))}
            </div>
          </section>
          <section className="CategoryGroup-section">
            <h2>Öne çıkan ve popüler konular</h2>
            {this.loading ? (
              <LoadingIndicator />
            ) : (
              <div className="CategoryGroup-discussions">
                {this.featured.map((discussion) => (
                  <Link key={`featured-${discussion.id()}`} href={app.route.discussion(discussion)}>
                    <strong>{discussion.title()}</strong>
                    <span>{Number(discussion.commentCount() || 0).toLocaleString('tr-TR')} yanıt</span>
                  </Link>
                ))}
              </div>
            )}
            <Link className="Button" href={`/?q=${encodeURIComponent(group.name)}`}>
              Yeni konuları göster
            </Link>
          </section>
          <section className="CategoryGroup-section">
            <h2>İlgili sözlük kategorileri</h2>
            <div className="CategoryGroup-glossary">
              {(group.glossary || []).map((slug) => (
                <a key={`group-glossary-${slug}`} href={`/sozluk/kategori/${encodeURIComponent(slug)}`}>
                  <i className="fas fa-book" /> Sözlük kategorisini keşfet
                </a>
              ))}
            </div>
            <a className="Button Button--primary" href={`/sozluk/ara?q=${encodeURIComponent(group.name)}`}>
              Öne çıkan terimler
            </a>
          </section>
        </main>
      </div>
    );
  }
}

function localDiscussionThumbnail(src) {
  if (typeof src !== "string" || !src.trim()) return "";
  try {
    const url = new URL(src.trim(), window.location.origin);
    return ["http:", "https:"].includes(url.protocol) && url.origin === window.location.origin ? url.href : "";
  } catch (_) { return ""; }
}

function discussionThumbnail(discussion) {
  const id = String(discussion?.id?.() || '');
  const mapped = window.__gardenselDiscussionThumbs?.[id];
  if (typeof mapped === 'string' && mapped.trim()) return localDiscussionThumbnail(mapped);
  const resolved = discussion?.attribute?.('gardenselThumbnail');
  if (typeof resolved === 'string' && resolved.trim()) return localDiscussionThumbnail(resolved);
  const html = discussion?.firstPost?.()?.contentHtml?.();
  if (typeof html !== 'string' || !html.includes('<img')) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const image = doc.querySelector('img[src]');
  if (!image) return '';
  try {
    const url = new URL(image.getAttribute('src'), window.location.origin);
    return localDiscussionThumbnail(url.href);
  } catch (_) {
    return '';
  }
}

function ensureGardenselPortalAssets() {
  if (!document.querySelector('link[data-gardensel-portal-css]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/tarim-atlasi/assets/gardensel-portal-showcase.css?v=20260924-1';
    link.dataset.gardenselPortalCss = '1';
    document.head.appendChild(link);
  }
  if (!document.querySelector('script[data-gardensel-portal-js]')) {
    const script = document.createElement('script');
    script.src = '/tarim-atlasi/assets/gardensel-portal-showcase.js?v=20260924-1';
    script.defer = true;
    script.dataset.gardenselPortalJs = '1';
    document.head.appendChild(script);
  }
}

class GardenselPortalShowcase extends Component {
  oncreate() { ensureGardenselPortalAssets(); }
  view() {
    return <div className="gardensel-portal-showcase" data-gardensel-portal-showcase="1" />;
  }
}

class ForumShowcase extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.index = 0;
    this.destroyed = false;
    this.timer = window.setInterval(() => {
      if (this.destroyed || document.hidden || document.querySelector('.GardenselShowcase:hover')) return;
      const items = this.items();
      if (items.length > 1) { this.index = (this.index + 1) % items.length; redrawIfMounted(this); }
    }, 7000);
  }
  onremove() { this.destroyed = true; window.clearInterval(this.timer); }
  items() {
    return app.discussions.getPages().flatMap((page) => page.items || [])
      .filter((discussion) => discussionThumbnail(discussion))
      .slice(0, 10);
  }
  view() {
    const items = this.items();
    if (!items.length) return null;
    if (this.index >= items.length) this.index = 0;
    const discussion = items[this.index];
    return (
      <section className="GardenselShowcase" aria-label="Forumdan öne çıkan konular">
        <Link className="GardenselShowcase-stage" href={app.route.discussion(discussion)}>
          <img src={discussionThumbnail(discussion)} alt="" loading="eager" />
          <span className="GardenselShowcase-shade" />
          <span className="GardenselShowcase-copy"><small>Forumdan seçkiler</small><strong>{discussion.title()}</strong></span>
        </Link>
        <nav aria-label="Öne çıkan konu seçimi">
          {items.map((item, i) => <button type="button" className={i === this.index ? 'is-active' : ''} aria-label={`${i + 1}. konu`} onclick={() => { this.index = i; }}>{i + 1}</button>)}
        </nav>
      </section>
    );
  }
}

function homeDiscover() {
  const discussions = app.discussions
    .getPages()
    .flatMap((page) => page.items || [])
    .slice(0, 6);
  return (
    <section className="GardenselDiscover">
      <div className="GardenselSection-title">
        <div>
          <span className="eyebrow">Gardensel bilgi ağı</span>
          <h2>Keşfetmeye devam edin</h2>
        </div>
      </div>
      <div className="GardenselDiscover-grid">
        <Link href={`${app.route('index')}?sort=top`}>
          <i className="fas fa-fire" />
          <strong>Popüler Konular</strong>
          <span>Topluluğun en çok konuştuğu başlıklar</span>
        </Link>
        <a href="/sozluk/gunun-terimi">
          <i className="fas fa-calendar-day" />
          <strong>Günün Terimi</strong>
          <span>Her gün yeni bir tarım kavramı</span>
        </a>
        <a href="/sozluk/rastgele">
          <i className="fas fa-random" />
          <strong>Rastgele Bilgi</strong>
          <span>26.006 terim içinden bir keşif</span>
        </a>
        <Link href={app.route('gardenselCategories')}>
          <i className="fas fa-sitemap" />
          <strong>Kategori Dizini</strong>
          <span>{Number(categoryPayload().tag_count || 0).toLocaleString('tr-TR')} kategoriyi ağaç görünümünde inceleyin</span>
        </Link>
      </div>
      {discussions.length ? (
        <div className="GardenselMiniList">
          <h3>Son eklenenler</h3>
          {discussions.slice(0, 4).map((discussion) => (
            <Link key={`latest-discussion-${discussion.id()}`} href={app.route.discussion(discussion)}>{discussion.title()}</Link>
          ))}
        </div>
      ) : null}
    </section>
  );
}


class DiscussionQuickNav extends Component {
  maxPostNumber() {
    const discussion = this.attrs.discussion;

    return Math.max(
      1,
      Number(
        discussion?.lastPostNumber?.() ||
        discussion?.commentCount?.() ||
        1
      )
    );
  }

  goToPost(number) {
    const discussion = this.attrs.discussion;
    const max = this.maxPostNumber();
    const target = Math.max(1, Math.min(max, Number(number) || 1));

    m.route.set(app.route.discussion(discussion, target));
  }

  askPostNumber() {
    const max = this.maxPostNumber();
    const value = window.prompt(
      `Gitmek istediğiniz mesaj numarasını yazın (1-${max}):`
    );

    if (value === null) return;

    const number = Number.parseInt(String(value).trim(), 10);

    if (!Number.isFinite(number) || number < 1 || number > max) {
      window.alert(`1 ile ${max} arasında geçerli bir mesaj numarası yazın.`);
      return;
    }

    this.goToPost(number);
  }

  view() {
    const max = this.maxPostNumber();

    // Kısa konularda gereksiz kalabalık oluşturma.
    if (max < 50) return null;

    return (
      <nav
        className="GardenselDiscussionNav"
        aria-label="Uzun konu gezinme araçları"
      >
        <button
          type="button"
          className="Button"
          title="İlk mesaja git"
          onclick={() => this.goToPost(1)}
        >
          <i className="fas fa-step-backward" />
          <span>İlk</span>
        </button>

        <button
          type="button"
          className="Button"
          title="Mesaj numarasına git"
          onclick={() => this.askPostNumber()}
        >
          <i className="fas fa-hashtag" />
          <span>Mesaj</span>
        </button>

        <button
          type="button"
          className="Button Button--primary"
          title={`Son mesaja git (${max})`}
          onclick={() => this.goToPost(max)}
        >
          <i className="fas fa-step-forward" />
          <span>Son</span>
        </button>

        <button
          type="button"
          className="Button"
          title="Sayfanın başına dön"
          onclick={() =>
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          }
        >
          <i className="fas fa-arrow-up" />
          <span>Üst</span>
        </button>

        <small>{max.toLocaleString('tr-TR')} mesaj</small>
      </nav>
    );
  }
}

if (!window.__gardenselDiscussionThumbs) {
  window.__gardenselDiscussionThumbs = {};
  fetch('/assets/discussion-thumbs/home-map.json?v=20260930b', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : {}))
    .then((data) => {
      window.__gardenselDiscussionThumbs = data || {};
      m.redraw();
    })
    .catch(() => {});
}

app.initializers.add('gardensel-ads', () => {
  app.routes.gardenselCategories = { path: '/kategoriler', component: CategoryDirectory };
  app.routes.gardenselCategoryGroup = { path: '/kategoriler/:group', component: CategoryGroupPage };
  extend(Search.prototype, 'sourceItems', (items) => items.add('gardenselGlossary', new GlossarySearchSource(), 15));
  extend(HeaderPrimary.prototype, 'items', (items) => {
    items.add(
      'gardenselForum',
      <LinkButton href="/" icon="fas fa-comments">
        {t('nav.forum', 'Forum')}
      </LinkButton>,
      30
    );
    items.add(
      'gardenselCategories',
      <LinkButton href={app.route('gardenselCategories')} icon="fas fa-sitemap">
        {t('nav.categories', 'Kategoriler')}
      </LinkButton>,
      20
    );
    items.add(
      'gardenselGlossary',
      <a className="Button Button--link" href="/sozluk/">
        <i className="icon fas fa-book" /> <span className="Button-label">{t('nav.glossary', 'Sözlük')}</span>
      </a>,
      10
    );
  });
  extend(DiscussionHero.prototype, 'items', function (items) {
    const discussion = this.attrs.discussion;
    const primaryTag = primaryDiscussionTag(discussion);
    const parent = primaryTag?.parent?.();
    items.add(
      'gardenselBreadcrumb',
      <nav className="GardenselBreadcrumb GardenselBreadcrumb--hero" aria-label="Breadcrumb">
        <Link href="/">Forum</Link>
        <span>›</span>
        {parent ? <Link href={app.route.tag(parent)}>{parent.name()}</Link> : null}
        {primaryTag ? <Link href={app.route.tag(primaryTag)}>{primaryTag.name()}</Link> : null}
      </nav>,
      30
    );
    if (discussion?.canRename?.()) {
      items.add(
        'gardenselTitleRename',
        <button
          type="button"
          className="Button Button--icon Button--link GardenselTitleRename"
          aria-label={t('discussion.rename_title', 'Konu başlığını düzenle')}
          title={t('discussion.rename_title', 'Konu başlığını düzenle')}
          onclick={() => DiscussionControls.renameAction.call(discussion)}
        >
          <i aria-hidden="true" className="icon fas fa-pencil-alt" />
        </button>,
        -5
      );
    }
  });
  extend(EditPostComposer.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    const discussion = post?.discussion?.();

    // Reuse the core pencil in the first post edit-composer heading.
    if (Number(post?.number?.()) !== 1 || !discussion?.canRename?.() || !items.has('title')) return;

    const title = items.get('title');
    const pencil = title?.children?.find(
      (child) => child?.attrs && String(child.attrs.className || child.attrs.class || '').includes('fa-pencil-alt')
    );
    if (!pencil) return;

    const rename = (event) => {
      event.preventDefault();
      event.stopPropagation();
      DiscussionControls.renameAction.call(discussion);
    };

    pencil.attrs.className = `${pencil.attrs.className || pencil.attrs.class || ''} GardenselFirstPostTitleRename`.trim();
    pencil.attrs.role = 'button';
    pencil.attrs.tabindex = 0;
    pencil.attrs['aria-label'] = t('discussion.rename_title', 'Konu başlığını düzenle');
    pencil.attrs.title = t('discussion.rename_title', 'Konu başlığını düzenle');
    pencil.attrs.onclick = rename;
    pencil.attrs.onkeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') rename(event);
    };
  });
  extend(WelcomeHero.prototype, 'contentItems', (items) => {
    items.remove('title');
    items.remove('subtitle');
    items.add('gardenselPortalLead', <GardenselPortalShowcase />, 80);
  });
  extend(IndexPage.prototype, 'navItems', (items) => {
    items.add(
      'gardenselLatest',
      <LinkButton href="/" icon="fas fa-clock">
        Son Konular
      </LinkButton>,
      95
    );
    items.add(
      'gardenselPopular',
      <LinkButton href="/?sort=top" icon="fas fa-fire">
        Popüler Konular
      </LinkButton>,
      94
    );
    items.add(
      'gardenselBeginners',
      <LinkButton href="/?q=yeni başlayan" icon="fas fa-seedling">
        Yeni Başlayanlar
      </LinkButton>,
      93
    );
    items.add(
      'gardenselRandomTerm',
      <a className="Button Button--link" href="/sozluk/rastgele">
        <i className="icon fas fa-random" /> Rastgele Terim
      </a>,
      92
    );
    items.add(
      'gardenselDayTerm',
      <a className="Button Button--link" href="/sozluk/gunun-terimi">
        <i className="icon fas fa-calendar-day" /> Günün Terimi
      </a>,
      91
    );
  });
  extend(IndexPage.prototype, 'contentItems', (items) => {
    if (placementEnabled('index')) items.add('gardenselPromoIndex', <div className="gardensel-promo-slot">{promoCard('index')}</div>, 95);
    items.add(
      'gardenselAbout',
      <section className="GardenselAbout">
        <span className="GardenselHero-kicker">Bilgi · Deneyim · Topluluk</span>
        <h2>Türkiye'nin En Büyük Tarım Bilgi Topluluğu</h2>
        <p>Forum, tarım sözlüğü ve binlerce uzmanlık başlığı tek bilgi ağında.</p>
        <nav>
          <Link href={app.route('index')}>Forum</Link>
          <a href="/sozluk/">Sözlük</a>
          <a href="/tr/">Bitki Veritabanı</a>
          <Link href={app.route('index') + '?q=' + encodeURIComponent('tarım')}>Tarım Bilgileri</Link>
          <Link href={app.route('index') + '?q=' + encodeURIComponent('peyzaj')}>Peyzaj</Link>
          <Link href={app.route('index') + '?q=' + encodeURIComponent('orman')}>Orman</Link>
        </nav>
      </section>,
      20
    );
    items.add('gardenselDiscover', homeDiscover(), 70);
    items.add('gardenselKnowledge', <HomeKnowledge />, 60);
  });
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    const image = discussionThumbnail(this.attrs.discussion);
    if (!image) return;
    items.add(
      'gardenselThumbnail',
      <span className="GardenselDiscussionThumb" aria-hidden="true"><img src={image} alt="" loading="lazy" onerror={(e) => e.target.parentElement?.remove()} /></span>,
      85
    );
  });
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const discussion = this.discussion;

    if (!discussion) return;

    const total = Number(
      discussion.lastPostNumber?.() ||
      discussion.commentCount?.() ||
      0
    );

    if (total < 50) return;

    items.add(
      'gardenselDiscussionQuickNav',
      <DiscussionQuickNav discussion={discussion} />,
      65
    );
  });

  extend(PostStream.prototype, 'afterFirstPostItems', function (items) {
    if (!this.discussion) return;
    items.add(
      'gardenselAfterFirstPost',
      <div key="gardenselAfterFirstPost" className="GardenselAfterFirstPost">
        {placementEnabled('after_first_post') ? promoCard('after-first-post') : null}
        <DiscussionHub discussion={this.discussion} />
      </div>,
      20
    );
  });
  extend(PostStream.prototype, 'endItems', function (items) {
    if (Number(this.discussion?.commentCount?.() || 0) >= 15 && placementEnabled('long_discussion'))
      items.add(
        'gardenselPromoLongDiscussion',
        <div key="gardenselPromoLongDiscussion" className="PostStream-item gardensel-promo-slot">
          {promoCard('long-discussion')}
        </div>
      );
  });
  let compact = false;
  const updateHeader = () => {
    const next = window.scrollY > 42;
    if (next !== compact) {
      compact = next;
      document.body.classList.toggle('Header--compact', compact);
    }
  };
  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();
});

export default {};
